import { useState, useEffect } from 'react';
import './FeedElement.css';
import { picturesAPI, usersAPI } from './services/api';
import UserAvatar from './UserAvatar';

function FeedElement({ feedItem, currentUser, onAvatarClick }) {
  const [postAuthor, setPostAuthor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [groupedUsersData, setGroupedUsersData] = useState([]);

  useEffect(() => {
    const loadPostAuthor = async () => {
      if (!feedItem.userId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const user = await usersAPI.getById(feedItem.userId);
        setPostAuthor(user);
      } catch (error) {
        console.error('Error loading post author:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPostAuthor();
  }, [feedItem.userId]);

  useEffect(() => {
    const loadGroupedUsers = async () => {
      if (!feedItem.groupedUsers || feedItem.groupedUsers.length === 0) {
        return;
      }

      try {
        const userPromises = feedItem.groupedUsers.map(gu =>
          usersAPI.getById(gu.userId)
        );
        const users = await Promise.all(userPromises);
        setGroupedUsersData(users);
      } catch (error) {
        console.error('Error loading grouped users:', error);
      }
    };

    loadGroupedUsers();
  }, [feedItem.groupedUsers]);
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('bg-BG', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatEventDateTime = (dateString) => {
    const date = new Date(dateString);
    const options = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return date.toLocaleDateString('bg-BG', options);
  };

  // Render avatar using UserAvatar component
  const renderAvatar = () => {
    if (loading || !postAuthor) {
      // Show loading placeholder
      return (
        <div className="feed-avatar default-avatar">
          <div className="avatar-picture">...</div>
        </div>
      );
    }

    const handleAvatarClick = () => {
      if (onAvatarClick && feedItem.userId) {
        onAvatarClick(feedItem.userId);
      }
    };

    return (
      <div onClick={handleAvatarClick} style={{ cursor: 'pointer' }}>
        <UserAvatar user={postAuthor} size="small" showName={false} />
      </div>
    );
  };

  // Render avatars for grouped users
  const renderGroupedAvatars = () => {
    if (groupedUsersData.length === 0) {
      return (
        <div className="feed-avatar default-avatar">
          <div className="avatar-picture">...</div>
        </div>
      );
    }

    return (
      <div className="grouped-avatars">
        {groupedUsersData.map((user) => {
          const handleAvatarClick = () => {
            if (onAvatarClick && user.id) {
              onAvatarClick(user.id);
            }
          };

          return (
            <div key={user.id} onClick={handleAvatarClick} style={{ cursor: 'pointer' }}>
              <UserAvatar user={user} size="small" showName={false} />
            </div>
          );
        })}
      </div>
    );
  };

  const isOwnPost = currentUser && feedItem.userId === currentUser.id;
  const isGroupedEvent = feedItem.groupedUsers && feedItem.groupedUsers.length > 0;
  const isEventPost = feedItem.eventId && feedItem.eventName;

  return (
    <div className="feed-element">
      <div className="feed-header">
        <div className="feed-user-info">
          {isGroupedEvent ? (
            <>
              {renderGroupedAvatars()}
            </>
          ) : (
            <>
              {renderAvatar()}
            </>
          )}
          {isGroupedEvent ? (
            <div className="feed-description">
              {feedItem.groupedUsers.length === 1 ? (
                <p>
                  <strong>{feedItem.groupedUsers[0].displayName}</strong> • <strong className="feed-action">is attending</strong>
                </p>
              ) : (
                <p>
                  <strong>{feedItem.groupedUsers.map(u => u.displayName).join(', ')}</strong> • <strong className="feed-action">are attending</strong>
                </p>
              )}
            </div>
          ) : (
            <div className="feed-description">
              {/* Badge display logic:
                  - If anonymous: "Person received a badge" (no sender)
                  - If offended but not anonymous: "Person received a badge from Sender" (no message shown above)
                  - If public (not anonymous, not offended): "Person received a badge from Sender" (message shown above) */}
              {feedItem.badgeId ? (
                feedItem.isAnonymousBadge ? (
                  <p>
                    <strong>{feedItem.userDisplayName}</strong> • <strong className="feed-action">received a badge</strong>
                  </p>
                ) : (
                  <p>
                    <strong>{feedItem.userDisplayName}</strong> • <strong className="feed-action">received a badge</strong> from{' '}
                    <strong>{feedItem.sendBadgeUserDisplayName || 'Someone'}</strong>
                  </p>
                )
              ) : isEventPost ? (
                feedItem.userId === feedItem.eventOrganizerId ? (
                  <p>
                    <strong>{feedItem.userDisplayName}</strong> • <strong className="feed-action">created a gathering</strong>
                    {feedItem.communityEventId && <span> (based on a community event)</span>}
                  </p>
                ) : (
                  <p>
                    <strong>{feedItem.userDisplayName}</strong> • <strong className="feed-action">is attending</strong>
                  </p>
                )
              ) : feedItem.pictureId ? (
                <p>
                  <strong>{feedItem.userDisplayName}</strong> • <strong className="feed-action">shared a photo</strong>
                </p>
              ) : feedItem.externalLink ? (
                <p>
                  <strong>{feedItem.userDisplayName}</strong> • <strong className="feed-action">shared a link</strong>
                </p>
              ) : feedItem.postDescription ? (
                <p>
                  <strong>{feedItem.userDisplayName}</strong> • <strong className="feed-action">posted an update</strong>
                </p>
              ) : (
                <p>
                  <strong>{feedItem.userDisplayName}</strong> • <strong className="feed-action">posted an update</strong>
                </p>
              )}
            </div>
          )}
        </div>
        <span className="feed-date">
          {isEventPost && feedItem.eventDateTime
            ? formatEventDateTime(feedItem.eventDateTime)
            : formatDate(feedItem.createdAt)}
        </span>
      </div>

      {/* Badge post description - only show if not anonymous and not offended */}
      {feedItem.postDescription && !feedItem.isAnonymousBadge && !feedItem.isBadgeOffended && (
        <div className="feed-post-description">
          <p>{feedItem.postDescription}</p>
        </div>
      )}
      {/* Badge message - only show if not anonymous and not offended */}
      {feedItem.badgeMessage && (
        <div className="feed-badge-message">
          <p><em>"{feedItem.badgeMessage}"</em></p>
        </div>
      )}

      {feedItem.pictureSysName && feedItem.pictureUsername && (
        <div className="feed-picture">
          <img
            src={picturesAPI.getImage(feedItem.pictureSysName, feedItem.pictureUsername)}
            alt="Feed content"
            className="feed-image"
          />
        </div>
      )}

      {feedItem.eventName && (
        <div className="feed-event">
          <span className="event-icon">📅</span>
          <div className="event-details">
            <span className="event-name">{feedItem.eventName}</span>
          </div>
        </div>
      )}

      {feedItem.eventPictureSysName && (feedItem.eventCommunityEventOrganizerUsername || feedItem.eventOrganizerUsername) && (
        <div className="feed-picture">
          <img
            src={picturesAPI.getImage(
              feedItem.eventPictureSysName,
              feedItem.eventCommunityEventOrganizerUsername || feedItem.eventOrganizerUsername
            )}
            alt="Event"
            className="feed-image"
          />
        </div>
      )}

      {feedItem.externalLink && (
        <div className="feed-link">
          <a
            href={feedItem.externalLink}
            target="_blank"
            rel="noopener noreferrer"
            className="external-link"
          >
            🔗 {feedItem.externalLink}
          </a>
        </div>
      )}
      
    </div>
  );
}

export default FeedElement;
